/**
 * Bouton "maintenir pour confirmer" et fenetre de confirmation qui l'utilise.
 *
 * 1.59.0.0 (demande du club : "certaines fonctions de React Bits sont interessantes pour securiser
 * des actions critiques, le hold button par exemple") : une action irreversible ne part plus sur un
 * simple clic - il faut garder le bouton enfonce 1,5 s, une jauge montre l'avancement, relacher
 * avant annule. Au clavier : maintenir Espace ou Entree. Ecrit sans dependance.
 *
 * `confirmerMaintenu({ titre, texte, libelle })` s'appelle comme window.confirm mais rend une
 * promesse (true si confirme) : il remplace un confirm() existant sans reecrire l'ecran.
 * L'hote <ConfirmationMaintenueHote /> est monte une fois dans App.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';

const DUREE_MS = 1500;

export const BoutonMaintenir = ({ onConfirme, children = 'Maintenir pour confirmer', danger = true, duree = DUREE_MS, disabled = false, className = '', ...props }) => {
  const [progres, setProgres] = useState(0);
  const debut = useRef(0);
  const raf = useRef(0);
  const fait = useRef(false);

  const arreter = useCallback(() => {
    cancelAnimationFrame(raf.current);
    debut.current = 0;
    if (!fait.current) setProgres(0);
  }, []);

  const avancer = useCallback((maintenant) => {
    if (!debut.current) debut.current = maintenant;
    const p = Math.min(1, (maintenant - debut.current) / duree);
    setProgres(p);
    if (p >= 1) {
      fait.current = true;
      onConfirme && onConfirme();
      return;
    }
    raf.current = requestAnimationFrame(avancer);
  }, [duree, onConfirme]);

  const commencer = useCallback(() => {
    if (disabled || fait.current || debut.current) return;
    raf.current = requestAnimationFrame(avancer);
  }, [avancer, disabled]);

  useEffect(() => () => cancelAnimationFrame(raf.current), []);

  // 1.59.0.1 : couleurs du theme (destructive + son texte), lisibles en clair comme en sombre -
  // en sombre, Balise remplace le rouge par un rose clair et le texte blanc n'y etait plus lisible.
  const couleur = danger ? 'bg-destructive text-destructive-foreground hover:opacity-90' : 'bg-primary text-primary-foreground';
  const enCours = progres > 0 && progres < 1;
  return (
    <button
      type="button"
      disabled={disabled}
      onPointerDown={(e) => { e.preventDefault(); commencer(); }}
      onPointerUp={arreter}
      onPointerLeave={arreter}
      onPointerCancel={arreter}
      onKeyDown={(e) => { if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) { e.preventDefault(); commencer(); } }}
      onKeyUp={(e) => { if (e.key === ' ' || e.key === 'Enter') arreter(); }}
      onContextMenu={(e) => e.preventDefault()}
      aria-label={typeof children === 'string' ? `${children} (maintenir enfoncé)` : 'Maintenir enfoncé pour confirmer'}
      className={`relative overflow-hidden select-none touch-none inline-flex items-center justify-center rounded-md px-4 h-10 text-sm font-semibold transition-colors disabled:opacity-50 ${couleur} ${className}`}
      data-testid="bouton-maintenir"
      {...props}
    >
      <span aria-hidden="true" className="absolute inset-y-0 left-0 bg-black/25 dark:bg-white/25" style={{ width: `${progres * 100}%` }} />
      {/* 1.76.1.0 (signalement du club : "le bouton se redimensionne suite au changement de texte,
          on se retrouve parfois hors du bouton") : "Maintenez…" est plus court que le libelle, le
          bouton retrecissait sous le pointeur et onPointerLeave annulait. Les deux libelles sont
          superposes dans la meme case de grille (un seul visible) : largeur = le plus long, fixe. */}
      <span className="relative grid justify-items-center">
        <span className={`col-start-1 row-start-1 ${enCours ? 'invisible' : ''}`}>{children}</span>
        <span aria-hidden="true" className={`col-start-1 row-start-1 ${enCours ? '' : 'invisible'}`}>Maintenez…</span>
      </span>
    </button>
  );
};

let demander = null;

/** Comme window.confirm, en promesse : true si l'action est confirmee en maintenant le bouton. */
export const confirmerMaintenu = (options = {}) => {
  if (!demander) return Promise.resolve(window.confirm(options.texte || options.titre || 'Confirmer ?'));
  return demander(options);
};

export const ConfirmationMaintenueHote = () => {
  const [etat, setEtat] = useState(null);   // { titre, texte, libelle, resoudre }

  useEffect(() => {
    demander = (options) => new Promise((resoudre) => setEtat({ ...options, resoudre }));
    return () => { demander = null; };
  }, []);

  const fermer = (resultat) => {
    if (etat) etat.resoudre(resultat);
    setEtat(null);
  };

  return (
    <Dialog open={!!etat} onOpenChange={(o) => { if (!o) fermer(false); }}>
      <DialogContent className="sm:max-w-md" data-testid="confirmation-maintenue">
        <DialogHeader>
          <DialogTitle>{etat?.titre || 'Action irréversible'}</DialogTitle>
          {etat?.texte && <DialogDescription className="whitespace-pre-line">{etat.texte}</DialogDescription>}
        </DialogHeader>
        <p className="text-sm text-muted-foreground">Gardez le bouton enfoncé pour confirmer ; relâchez pour annuler.</p>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => fermer(false)} data-testid="confirmation-maintenue-annuler">Annuler</Button>
          {etat && <BoutonMaintenir key={etat.titre + (etat.texte || '')} onConfirme={() => fermer(true)}>{etat.libelle || 'Maintenir pour confirmer'}</BoutonMaintenir>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BoutonMaintenir;
